import { Link, useSearchParams } from 'react-router-dom'
import { Home, MapPin, Truck, Clock, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { usePageTitle, setDocumentTitle } from '../utils/translation'
import { serviceAreaCoordinates, defaultCenter, ringRoadBounds } from '../lib/map/constants'

export default function DeliveryRange() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const pageTitle = usePageTitle('pages.deliveryRange.title', 'Delivery Range')
  
  // Check if user came from a specific source
  const fromSource = searchParams.get('from')
  
  // Map related state
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  
  useEffect(() => {
    setDocumentTitle(pageTitle)
  }, [pageTitle])

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true)
      return
    }
    
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => setMapLoaded(true)
    document.head.appendChild(script)
    
    return () => {
      try {
        document.head.removeChild(script)
      } catch (e) {
        // Script might already be removed
      }
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !window.google || !window.google.maps || !mapRef.current || mapInstanceRef.current) {
      return
    }

    // Use actual default center from constants
    const chiangMaiCenter = defaultCenter
    
    // Create map
    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: chiangMaiCenter,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    })

    mapInstanceRef.current = map

    // Draw the actual service area polygon using real coordinates
    const serviceAreaPolygon = new window.google.maps.Polygon({
      paths: serviceAreaCoordinates,
      strokeColor: '#22C55E', // Green for service area
      strokeOpacity: 0.8,
      strokeWeight: 3,
      fillColor: '#22C55E',
      fillOpacity: 0.15,
      map: map
    })

    // Add service area boundary outline for better visibility
    const boundaryPolygon = new window.google.maps.Polygon({
      paths: serviceAreaCoordinates,
      strokeColor: '#059669', // Darker green for border
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: 'transparent',
      fillOpacity: 0,
      map: map
    })



    // Fit map to show the entire service area
    const bounds = new window.google.maps.LatLngBounds()
    serviceAreaCoordinates.forEach(coord => {
      bounds.extend(coord)
    })
    map.fitBounds(bounds)

  }, [mapLoaded])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="flex items-center px-4 py-4">
          <Link 
            to={fromSource ? `/${fromSource}` : "/"} 
            className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-600 mr-4 shadow-sm hover:bg-amber-700 transition-colors"
            aria-label="Return to home"
          >
            <Home size={20} className="text-white" />
          </Link>
          <h1 className="font-bold text-xl text-amber-800">{t('pages.deliveryRange.title')}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-5 py-8">
        {/* Service Area Overview */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-4">{t('pages.deliveryRange.serviceArea.title')}</h2>
          <div className="flex items-start mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
              <MapPin className="w-5 h-5 text-amber-700" />
            </div>
            <div className="ml-4">
              <h3 className="font-semibold text-amber-900">{t('pages.deliveryRange.serviceArea.currentLocation')}</h3>
              <p className="text-amber-800 text-lg">{t('pages.deliveryRange.serviceArea.chiangMai')}</p>
              <p className="text-sm text-amber-600 mt-1">{t('pages.deliveryRange.serviceArea.description')}</p>
            </div>
          </div>
        </div>

        {/* Interactive Map Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-4">{t('pages.deliveryRange.map.title')}</h2>
          <p className="text-amber-800 mb-4">{t('pages.deliveryRange.map.description')}</p>
          
          <div 
            ref={mapRef}
            className="w-full h-[400px] rounded-lg border border-gray-200 bg-gray-100 relative"
            style={{ minHeight: '400px' }}
          >
            {!mapLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-2"></div>
                <p className="text-gray-500 text-sm">{t('pages.deliveryRange.map.loading')}</p>
              </div>
            )}
            {mapLoaded && !window.google && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
                <p className="text-gray-500 text-sm">{t('pages.deliveryRange.map.loadingMaps')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Coverage Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-6">{t('pages.deliveryRange.coverage.title')}</h2>
          
          <div className="space-y-5">
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                <CheckCircle className="w-5 h-5 text-green-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.deliveryRange.coverage.withinRingRoad')}</h3>
                <p className="text-amber-800">{t('pages.deliveryRange.coverage.withinRingRoadDescription')}</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                <MapPin className="w-5 h-5 text-blue-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.deliveryRange.coverage.nearbyAreas')}</h3>
                <p className="text-amber-800">{t('pages.deliveryRange.coverage.nearbyAreasDescription')}</p>
                <ul className="text-sm text-amber-600 mt-2 space-y-1">
                  <li>• {t('pages.deliveryRange.coverage.areas.maeRim')}</li>
                  <li>• {t('pages.deliveryRange.coverage.areas.hangDong')}</li>
                  <li>• {t('pages.deliveryRange.coverage.areas.saraphi')}</li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                <Truck className="w-5 h-5 text-amber-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.deliveryRange.coverage.deliverySchedule')}</h3>
                <p className="text-amber-800">{t('pages.deliveryRange.coverage.deliveryScheduleDescription')}</p>
                <div className="text-sm text-amber-600 mt-2">
                  <p>{t('pages.deliveryRange.coverage.timeSlots.morning')}</p>
                  <p>{t('pages.deliveryRange.coverage.timeSlots.afternoon')}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-1">
                <Clock className="w-5 h-5 text-purple-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.deliveryRange.coverage.deliveryDays')}</h3>
                <p className="text-amber-800">{t('pages.deliveryRange.coverage.deliveryDaysDescription')}</p>
                <p className="text-sm text-amber-600 mt-1">{t('pages.deliveryRange.coverage.deliveryDaysNote')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* How to Check Your Area */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-4">{t('pages.deliveryRange.howToCheck.title')}</h2>
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-sm flex items-center justify-center flex-shrink-0 mt-1">1</span>
              <p className="ml-3 text-amber-800">{t('pages.deliveryRange.howToCheck.step1')}</p>
            </div>
            <div className="flex items-start">
              <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-sm flex items-center justify-center flex-shrink-0 mt-1">2</span>
              <p className="ml-3 text-amber-800">{t('pages.deliveryRange.howToCheck.step2')}</p>
            </div>
            <div className="flex items-start">
              <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-sm flex items-center justify-center flex-shrink-0 mt-1">3</span>
              <p className="ml-3 text-amber-800">{t('pages.deliveryRange.howToCheck.step3')}</p>
            </div>
          </div>
        </div>

        {/* Expansion Plans */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-bold text-amber-900 mb-4">{t('pages.deliveryRange.expansion.title')}</h2>
          <p className="text-amber-800 mb-4">{t('pages.deliveryRange.expansion.description')}</p>
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-amber-600 rounded-full mr-3"></div>
              <span className="text-amber-800">{t('pages.deliveryRange.expansion.bangkok')}</span>
              <span className="text-sm text-amber-600 ml-2">({t('pages.deliveryRange.expansion.comingSoon')})</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-amber-600 rounded-full mr-3"></div>
              <span className="text-amber-800">{t('pages.deliveryRange.expansion.huaHin')}</span>
              <span className="text-sm text-amber-600 ml-2">({t('pages.deliveryRange.expansion.comingSoon')})</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white py-6 px-5 border-t border-amber-100">
        <div className="flex flex-col items-center space-y-4">
          <p className="text-center text-xs text-amber-600">
            &copy; {new Date().getFullYear()} {t('pages.deliveryRange.footer.copyright')}
          </p>
          <div className="flex space-x-4">
            <Link to="/terms" className="text-xs text-amber-700">{t('pages.deliveryRange.footer.terms')}</Link>
            <Link to="/privacy" className="text-xs text-amber-700">{t('pages.deliveryRange.footer.privacy')}</Link>
            <Link to="/contact" className="text-xs text-amber-700">{t('pages.deliveryRange.footer.contact')}</Link>
          </div>
        </div>
      </footer>
    </div>
  )
} 